import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { ManwayFeature3D } from 'features/assetViz/objects/features/ManwayFeature3D'
import { NozzleFeature3D } from 'features/assetViz/objects/features/NozzleFeature3D'
import {
  planeGeometry,
  xWrappedShellGeometry,
} from 'features/assetViz/utils/geometryDefines'
import { extractScale } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import { GeckoAssetModel, GeckoPressureVesselShell } from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing a pressure vessel shell - a cylindrical component
 * with pressure vessel specific features like nozzles, manholes, and welds.
 *
 * @param model       The pressure vessel shell component model
 * @param assetModel  The asset model containing this shell
 * @param groupProps  Position, rotation, scale, and other 3D properties
 */
export const PressureVesselShellSurface3D: React.FC<
  {
    model: GeckoPressureVesselShell
    assetModel?: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale, manways, nozzles } = React.useMemo(() => {
    const bounds = model.componentBounds

    // For pressure vessel shell, wrap around X axis (circumferentially) like pipes
    const scale = extractScale(groupProps.scale).multiply(
      show3D
        ? new Vector3(
            model.radius.num, // radius
            model.componentBounds.height.num, // length
            model.radius.num // radius
          )
        : new Vector3(
            model.componentBounds.width.num,
            model.componentBounds.height.num,
            1.0
          )
    )

    return {
      bounds,
      scale,
      manways: model.manwaysArray,
      nozzles: model.nozzlesArray,
    }
  }, [model, groupProps.scale, show3D])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    return xWrappedShellGeometry // Wraps around X axis like pipes
  }, [show3D])

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}>
      {/* Render available pressure vessel shell features */}
      {manways.map((manway) => (
        <ManwayFeature3D key={manway.id} model={manway} />
      ))}
      {nozzles.map((nozzle) => (
        <NozzleFeature3D key={nozzle.id} model={nozzle} />
      ))}
    </ModelSurface3D>
  )
}
